/* global google */
import React, { useRef } from "react";
import { GoogleMap, Polygon, GroundOverlay } from "@react-google-maps/api";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";

const containerStyle = {
  width: "100%",
  height: "650px",
};

// Map of descriptions
const descriptions = {
  q1: "The first quartile (25%) of vegetation index values within AOI.",
  q3: "The third quartile (75%) of vegetation index values within AOI.",
  max: "The maximum vegetation index value within AOI.",
  min: "The minimum vegetation index value within AOI.",
  p10: "10th percentile of vegetation index values within AOI.",
  p90: "90th percentile of vegetation index values within AOI.",
  std: "Standard deviation - spread/variability of values.",
  median: "Median (50%) of vegetation index values within AOI.",
  average: "Average vegetation index value within AOI.",
  variance: "Variance of vegetation index values within AOI.",
  cloud: "Percent of cloud cover within AOI.",
  date: "Acquisition date of the scene.",
  notes: "Any extra description or notes for this scene.",
  ctime10: "Root zone soil moisture (for soil moisture only).",
};

// Convert key to Title Case
const formatKey = (key) =>
  key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const getColor = (value) => {
  if (value >= 0.9) return "#06653d";
  if (value >= 0.8) return "#11a75f";
  if (value >= 0.7) return "#81bf6c";
  if (value >= 0.6) return "#bae383";
  if (value >= 0.5) return "#e6f3a4";
  if (value >= 0.4) return "#fff0b5";
  if (value >= 0.3) return "#fbc07e";
  if (value >= 0.2) return "#f7885a";
  if (value >= 0.1) return "#ea4f3b";
  return "#ab0535";
};

export default function IndicesViewer({ result, coordinates, image }) {
  const mapRef = useRef(null);

  const polygonCoords = coordinates[0].map(([lng, lat]) => ({ lat, lng }));

  const center = polygonCoords.reduce(
    (acc, cur) => {
      acc.lat += cur.lat;
      acc.lng += cur.lng;
      return acc;
    },
    { lat: 0, lng: 0 }
  );
  center.lat /= polygonCoords.length;
  center.lng /= polygonCoords.length;

  const bounds = new google.maps.LatLngBounds();
  polygonCoords.forEach((c) => bounds.extend(c));

  const onLoad = (map) => {
    mapRef.current = map;
    if (polygonCoords.length > 0) {
      const fitBounds = new google.maps.LatLngBounds();
      polygonCoords.forEach((c) => fitBounds.extend(c));
      map.fitBounds(fitBounds, 50);

      const listener = google.maps.event.addListener(map, "idle", function () {
        if (map.getZoom() > 17) map.setZoom(17);
        google.maps.event.removeListener(listener);
      });
    }
  };

  const isMobile = window.innerWidth <= 768;

  const legendStyle = {
    position: "absolute",
    left: isMobile ? 1 : 2,
    top: isMobile ? 0 : 0,
    background: "rgba(255,255,255,0.95)",
    padding: isMobile ? "6px" : "12px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? "4px" : "6px",
    fontSize: isMobile ? "10px" : "12px",
    width: isMobile ? "120px" : "150px",
    maxHeight: "650px",
    overflowY: "auto",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
    zIndex: 10,
    wordBreak: "break-word",
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <GoogleMap
        onLoad={onLoad}
        mapContainerStyle={containerStyle}
        center={center}
        zoom={16}
        options={{
          mapTypeId: "roadmap",
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
            position: google.maps.ControlPosition.TOP_RIGHT,
            mapTypeIds: ["roadmap", "satellite"],
          },
        }}
      >
        {image ? (
          <>
            <GroundOverlay key={image} url={image} bounds={bounds} opacity={5} />
            <Polygon
              paths={polygonCoords}
              options={{
                fillOpacity: 0,
                strokeColor: "black",
                strokeOpacity: 1,
                strokeWeight: 0,
              }}
            />
          </>
        ) : (
          <Polygon
            paths={polygonCoords}
            options={{
              fillColor: getColor(result?.average),
              fillOpacity: 0.6,
              strokeColor: "black",
              strokeOpacity: 1,
              strokeWeight: 0.5,
            }}
          />
        )}
      </GoogleMap>

      {/* Legend */}
      {result && !Array.isArray(result) && Object.keys(result).length > 0 && (
        <div style={legendStyle}>
          {(() => {
            const numericEntries = Object.entries(result).filter(
              ([key, val]) => typeof val === "number"
            );
            const dateEntry = Object.entries(result).find(
              ([key]) => key === "date"
            );

            return (
              <>
                {numericEntries.map(([key, val]) => (
                  <div key={key} style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontWeight: "bold",
                        fontSize: isMobile ? "10px" : "12px",
                      }}
                    >
                      <span>{formatKey(key)}</span>
                      {descriptions[key] && (
                        <OverlayTrigger
                          trigger="click"
                          placement="top"
                          rootClose
                          overlay={
                            <Popover id={`popover-${key}`}>
                              <Popover.Header as="h3">
                                {formatKey(key)}
                              </Popover.Header>
                              <Popover.Body>{descriptions[key]}</Popover.Body>
                            </Popover>
                          }
                        >
                          <InfoCircle size={14} style={{ cursor: "pointer" }} />
                        </OverlayTrigger>
                      )}
                    </div>

                    <div
                      style={{
                        background: "#eee",
                        height: "8px",
                        borderRadius: "4px",
                        overflow: "hidden",
                        marginTop: "2px",
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(100, Math.max(0, val * 100))}%`,
                          height: "100%",
                          background: getColor(val),
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    <div style={{ fontSize: "11px", color: "#555" }}>
                      {val.toFixed(3)}
                    </div>
                  </div>
                ))}

                {dateEntry && (
                  <div
                    key={dateEntry[0]}
                    style={{
                      marginTop: "10px",
                      paddingTop: "6px",
                      borderTop: "1px solid #ddd",
                      color: "#222",
                    }}
                  >
                    <p style={{ fontWeight: "bold", margin: "0 0 4px 0" }}>
                      {formatKey(dateEntry[0])}
                    </p>
                    <div style={{ fontSize: "12px", color: "#555" }}>
                      {dateEntry[1]}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
